import type { NestedKeySort } from '../TreeSelect.types';
import { SelectItem } from '@/types/ui';
import { useTreeSelectOptions } from './useTreeSelectOptions';
import { useTreeSelectHandlers } from './useTreeSelectHandlers';
import { useTreeSelectRendering } from './useTreeSelectRendering';
import { useTreeSelectVirtualization } from './useTreeSelectVirtualization';

type UseTreeSelectParams<T extends string> = {
  options: SelectItem<T>[];
  value: T[];
  onChange: (value: T[]) => void;
  collapseSingleChild?: boolean;
  selectionMode: 'standard' | 'indication';
  nestedKeySort: NestedKeySort;
  hideCheckboxes?: boolean;
  disabled?: boolean;
  treeListItemClassName?: string;
};

export function useTreeSelect<T extends string>({
  options,
  value,
  onChange,
  collapseSingleChild,
  selectionMode,
  nestedKeySort,
  hideCheckboxes,
  disabled,
  treeListItemClassName,
}: UseTreeSelectParams<T>) {
  const { fixedOptions, nestedOptions, descendantMap, disabledPathSet, sortedNestedOptions, valueLabelList } =
    useTreeSelectOptions<T>({
      options,
      value,
      collapseSingleChild,
      selectionMode,
      nestedKeySort,
    });

  const { searchInput, setSearchInput, searchResults, branchOverrides, handleClickOption, handleToggleBranch } =
    useTreeSelectHandlers<T>({ value, onChange, nestedOptions, descendantMap, selectionMode, hideCheckboxes });

  const { visibleRows, renderTreeRow } = useTreeSelectRendering<T>({
    nestedOptions: sortedNestedOptions,
    value,
    searchInput,
    searchResults,
    branchOverrides,
    selectionMode,
    disabled,
    disabledPathSet,
    hideCheckboxes,
    treeListItemClassName,
    handleClickOption,
    handleToggleBranch,
  });

  const virtualization = useTreeSelectVirtualization(visibleRows);

  return {
    fixedOptions,
    disabledPathSet,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    visibleRows,
    renderTreeRow,
    virtualization,
  };
}
